import localForage from 'localforage';
import { persistReducer } from 'redux-persist';
import { createSelector } from '@reduxjs/toolkit';
import { type RootState } from 'app/store';
import { type DeviceGroup, type DeviceGroupsState, type DeviceGroupsActionTypes } from 'app/store/types';

const initialState: DeviceGroupsState = {};

const deviceGroupsReducer = (state = initialState, action: DeviceGroupsActionTypes) => {
	switch (action.type) {
		case 'GET_LICENSE_GROUP_DATA_SUCCESS': {
			const {
				licenseGroupId,
				data: { deviceGroups },
			} = action.payload;

			let updatedGroups;
			if (deviceGroups) {
				// maintain order since not returned directly by api call
				const groups = { ...state[licenseGroupId]?.byId };

				const existingGroups = Object.keys(deviceGroups)
					.filter((id) => groups[id] && typeof groups[id]?.groupOrder === 'number')
					.reduce((result: { [key: string]: DeviceGroup }, id) => {
						const existingGroup = groups[id];
						result[id] = {
							...deviceGroups[id],
							groupOrder: existingGroup.groupOrder,
						};
						return result;
					}, {});

				const existingGroupOrders = Object.values(existingGroups)
					.filter((group: DeviceGroup) => typeof group.groupOrder === 'number')
					.map((group: DeviceGroup) => group.groupOrder!);
				const maxGroupOrder = existingGroupOrders.length > 0 ? Math.max(...existingGroupOrders) : -1;

				// Assign groupOrder to the rest of the groups
				const newGroups = Object.keys(deviceGroups)
					.filter((id) => !groups[id] || typeof groups[id]?.groupOrder !== 'number')
					.reduce((result: { [key: string]: DeviceGroup }, id, index) => {
						result[id] = {
							...deviceGroups[id],
							groupOrder: maxGroupOrder + 1 + index,
						};
						return result;
					}, {});

				updatedGroups = {
					...existingGroups,
					...newGroups,
				};
			}
			return {
				...state,
				[licenseGroupId]: {
					...state[licenseGroupId],
					...(updatedGroups && { byId: updatedGroups }),
				},
			};
		}
		case 'GET_DEVICE_GROUP_ORDER_SUCCESS': {
			const { order, licenseGroupId } = action.payload;
			const groups = { ...state[licenseGroupId].byId };

			// order list may not contain all groups
			const existingGroupIds = order.filter((groupId: string) => groups[groupId]);
			const updatedGroups: { [key: string]: DeviceGroup } = {};

			existingGroupIds.forEach((groupId: string, idx: number) => {
				const group = { ...groups[groupId] };
				group.groupOrder = idx + 1;
				updatedGroups[groupId] = group;
			});

			// assign other group order for non existing groups in order array
			const nonOrderedGroups = Object.keys(groups).filter((groupId) => !existingGroupIds.includes(groupId));

			nonOrderedGroups.forEach((groupId, idx) => {
				const group = { ...groups[groupId] };
				updatedGroups[groupId] = group;
			});

			return {
				...state,
				[licenseGroupId]: {
					byId: updatedGroups,
				},
			};
		}
		case 'LOGGED_OUT_USER':
		case 'PURGE_STATE':
			return initialState;
		default: {
			return state;
		}
	}
};

export default persistReducer(
	{
		key: 'deviceGroups',
		storage: localForage,
		whitelist: localStorage.getItem('persist') ? undefined : [],
	},
	deviceGroupsReducer,
);

// Selectors
export const getDeviceGroupsById = ({ app: { selectedLicenseGroupId }, deviceGroups }: RootState) =>
	deviceGroups[selectedLicenseGroupId].byId;

export const getDeviceGroups = createSelector([getDeviceGroupsById], (deviceGroupsById) =>
	Object.values(deviceGroupsById),
);

export const getDeviceGroupById = (deviceGroupId: DeviceGroup['id']) => (state: RootState) =>
	getDeviceGroupsById(state)[deviceGroupId];
